import { defineStore } from 'pinia'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import RealestateAPI from '@/modules/realestate/constants/realestate.api.uri.const.ts'
import { mappingValuesToForm, formatDataToSubmit } from '@/modules/realestate/helper/listing'

export enum ListingDetailActionType {
  FETCHING = 'fetching',
  FETCHED = 'fetched',
  FETCH_FAILED = 'fetch_failed',
  CREATING = 'creating',
  CREATED = 'created',
  CREATE_FAILED = 'create_failed',
  UPDATING = 'updating',
  UPDATED = 'updated',
  UPDATE_FAILED = 'update_failed',
  APPROVING = 'approving',
  APPROVED = 'approved',
  APPROVE_FAILED = 'approve_failed',
  REJECTING = 'rejecting',
  REJECTED = 'rejected',
  REJECT_FAILED = 'reject_failed',

  ATTRIBUTES_FETCHING = 'attributes_fetching',
  ATTRIBUTES_FETCHED = 'attributes_fetched',
  ATTRIBUTES_FETCH_FAILED = 'attributes_fetch_failed',
}

interface ListingDetail {
  [key: string]: any
}

interface ListingDetailState {
  listings: {
    [key: string]: {
      action_type: ListingDetailActionType
      action_message: string
      data: ListingDetail
      formData: any
    }
  }
  attributes?: {
    action_type: ListingDetailActionType
    data: any
  }
}

export const useListingDetailStore = defineStore('listing.detail', {
  state: (): ListingDetailState => ({
    listings: {},
    attributes: {
      action_type: ListingDetailActionType.ATTRIBUTES_FETCHING,
      data: {},
    },
  }),
  getters: {},
  actions: {
    async removeListingDetail(id: number) {
      return new Promise((resolve, reject) => {
        try {
          if (!this.listings[id]) {
            throw new Error('Listing not found')
          }
          delete this.listings[id]
          resolve(true)
        } catch (e) {
          console.error('Error removing listing detail:', e)
          reject(e)
        }
      })
    },
    async removeAllListingDetails() {
      return new Promise((resolve, reject) => {
        try {
          this.listings = {}
          resolve(true)
        } catch (e) {
          console.error('Error removing all listing details:', e)
          reject(e)
        }
      })
    },
    async fetchListingDetail(id: number, has_request_type?: boolean = false) {
      // if if the listing is already fetched, return it
      // const existListing = this.listings[id] && this.listings[id].data
      return new Promise(async (resolve, reject) => {
        try {
          // fetching
          this.listings[id] = {
            ...this.listings[id],
            action_type: ListingDetailActionType.FETCHING,
          }
          const res = await APIClient.get(
            RealestateAPI.listing.detail.replace(':id', id.toString()),
            {
              params: has_request_type
                ? {
                    has_request_type,
                  }
                : {},
            },
          )

          this.listings[id] = {
            action_type: ListingDetailActionType.FETCHED,
            action_message: res?.data?.message || '',
            data: {
              ...res.data.data,
              new_data: res.data.data?.new_data
                ? {
                    ...res.data.data.new_data,
                    photos: Array.isArray(res.data.data.new_data.photos)
                      ? res.data.data.new_data.photos
                      : Object.values(res.data.data.new_data.photos || {}),
                  }
                : null,
            } as ListingDetail,
            formData: {},
          }
          resolve(this.listings[id].data)
        } catch (e) {
          console.error('Error fetching listing detail:', e)
          this.listings[id].action_type = ListingDetailActionType.FETCH_FAILED
          this.listings[id].action_message = e.message || 'Failed to fetch listing detail'
          reject(e)
        }
      })
    },
    async approveListing(id: nunber, newData?: any) {
      return new Promise(async (resolve, reject) => {
        try {
          if (!this.listings[id]) {
            throw new Error('Listing not found')
          }
          if (this.listings[id].action_type === ListingDetailActionType.APPROVING) {
            throw new Error('Listing is already being approved')
          }
          this.listings[id] = {
            ...this.listings[id],
            action_type: ListingDetailActionType.APPROVING,
          }
          const data = this.listings[id].data
          const formValues = mappingValuesToForm(newData || data)
          const submitValues = formatDataToSubmit({
            ...formValues,
            update_type: 'approve',
          })
          const res = await APIClient.put(
            RealestateAPI.listing.update.replace(':id', data.id.toString()),
            submitValues,
          )
          this.listings[id] = {
            ...this.listings[id],
            action_type: ListingDetailActionType.APPROVED,
            action_message: res?.data?.message || '',
            data: {
              ...this.listings[id].data,
              ...submitValues,
              photos: data.photos,
              video: data.video,
              panorama: data.panorama,
              new_data: null,
            },
            formData: {},
          }
          resolve(this.listings[id].data)
        } catch (e) {
          console.error('Error update listing detail:', e)
          this.listings[id].action_type = ListingDetailActionType.APPROVE_FAILED
          this.listings[id].action_message = e.message || 'Failed to approve listing'
          reject(e)
        }
      })
    },
    async rejectListing(id: nunber) {
      return new Promise(async (resolve, reject) => {
        try {
          if (!this.listings[id]) {
            throw new Error('Listing not found')
          }
          if (this.listings[id].action_type === ListingDetailActionType.REJECTING) {
            throw new Error('Listing is already being rejected')
          }
          this.listings[id] = {
            ...this.listings[id],
            action_type: ListingDetailActionType.REJECTING,
          }
          const data = this.listings[id].data
          const formValues = mappingValuesToForm(data)
          const submitValues = formatDataToSubmit({
            ...formValues,
            update_type: 'reject',
          })
          const res = await APIClient.put(
            RealestateAPI.listing.update.replace(':id', id.toString()),
            submitValues,
          )
          this.listings[id] = {
            ...this.listings[id],
            action_type: ListingDetailActionType.REJECTED,
            action_message: res?.data?.message || '',
            data: {
              ...this.listings[id].data,
              ...submitValues,
              photos: data.photos,
              video: data.video,
              panorama: data.panorama,
              new_data: null,
            },
            formData: {},
          }
          resolve(this.listings[id].data)
        } catch (e) {
          console.error('Error update listing detail:', e)
          this.listings[id].action_type = ListingDetailActionType.REJECT_FAILED
          this.listings[id].action_message = e.message || 'Failed to reject listing'
          reject(e)
        }
      })
    },
    async setListingDetail(id: number, account: Object) {
      this.listings[id] = {
        action_type: ListingDetailActionType.FETCHED,
        action_message: '',
        account: { ...this.listings[id].account, ...account },
      }
    },
    async fetchAttributes() {
      if (this.attributes?.action_type === ListingDetailActionType.ATTRIBUTES_FETCHED) {
        return Promise.resolve(this.attributes.data)
      }
      return new Promise(async (resolve, reject) => {
        try {
          this.attributes = {
            action_type: ListingDetailActionType.ATTRIBUTES_FETCHING,
            data: {},
          }
          const res = await APIClient.get(RealestateAPI.attributes.list)

          this.attributes = {
            action_type: ListingDetailActionType.ATTRIBUTES_FETCHED,
            data: res.data,
          }
          return resolve(this.attributes.data)
        } catch (e) {
          console.error('Error fetching attributes:', e)
          this.attributes.action_type = ListingDetailActionType.ATTRIBUTES_FETCH_FAILED
          this.attributes.data = {}
          return reject(e)
        }
      })
    },
    async updateListingDetail(id: number, data: any) {
      return new Promise(async (resolve, reject) => {
        try {
          if (!this.listings[id]) {
            throw new Error('Listing not found')
          }
          if (this.listings[id].action_type === ListingDetailActionType.UPDATING) {
            throw new Error('Listing is already being rejected')
          }
          this.listings[id] = {
            ...this.listings[id],
            action_type: ListingDetailActionType.UPDATING,
          }
          const submitValues = formatDataToSubmit({
            ...data,
            update_type: 'update',
          })
          const res = await APIClient.put(
            RealestateAPI.listing.update.replace(':id', id.toString()),
            submitValues,
          )
          this.listings[id] = {
            ...this.listings[id],
            action_type: ListingDetailActionType.UPDATED,
            action_message: res?.data?.message || '',
            data: {
              ...this.listings[id].data,
              ...submitValues,
              photos: data.photos,
              video: data.video,
              panorama: data.panorama,
              contract: submitValues?.contract_id
                ? {
                    id: +submitValues.contract_id,
                  }
                : null,
            },
            formData: {},
          }
          resolve(this.listings[id].data)
        } catch (e) {
          console.error('Error update listing detail:', e)
          this.listings[id].action_type = ListingDetailActionType.UPDATE_FAILED
          this.listings[id].action_message = e.message || 'Failed to update listing detail'
          reject(e)
        }
      })
    },
  },
})
